import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LuMapPin, LuSearch } from 'react-icons/lu';
import Icon from '../Icon';
import {
  autocomplete,
  newSessionToken,
  placeDetails,
  type Prediction,
} from '../../../api/location';
import styles from './LocationSearch.module.css';

export type LocationResult = {
  /** Human-readable place, saved to the enquiry's origin / destination. */
  formattedAddress: string;
  latitude: number;
  longitude: number;
  city: string;
  state: string | null;
  country: string;
  countryCode: string | null;
  placeId: string | null;
};

type Props = {
  value: LocationResult | null;
  onChange: (loc: LocationResult | null) => void;
  placeholder?: string;
  invalid?: boolean;
  /** The caller's input styling, so this sits flush with its sibling fields. */
  inputClassName?: string;
  'aria-label'?: string;
};

/** Splits "Hyde Park, London, UK" into its head and the rest. */
const splitDescription = (description: string) => {
  const [head, ...rest] = description.split(',');
  return { main: head?.trim() || description, sub: rest.join(',').trim() };
};

export const LocationSearch: React.FC<Props> = ({
  value,
  onChange,
  placeholder = 'Search by postcode, area...',
  invalid,
  inputClassName = '',
  'aria-label': ariaLabel,
}) => {
  const [query, setQuery] = useState('');
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionRef = useRef<string>(newSessionToken());
  // Guards against a slow response for an earlier keystroke overwriting the
  // predictions for a later one.
  const requestRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const search = useCallback((next: string) => {
    setQuery(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!next.trim()) {
      setPredictions([]);
      setOpen(false);
      setLoading(false);
      return;
    }

    // Debounced so a typed word costs one Places call, not one per keystroke.
    debounceRef.current = setTimeout(async () => {
      const id = ++requestRef.current;
      setLoading(true);
      try {
        const list = await autocomplete(next.trim(), sessionRef.current);
        if (id !== requestRef.current) return;
        setPredictions(list);
        setOpen(true);
      } finally {
        if (id === requestRef.current) setLoading(false);
      }
    }, 400);
  }, []);

  const select = useCallback(
    async (p: Prediction) => {
      setOpen(false);
      setLoading(true);
      try {
        const details = await placeDetails(p.placeId, sessionRef.current);
        if (!details) return;
        const { main, sub } = splitDescription(p.description);
        onChange({
          formattedAddress: details.formattedAddress || p.description,
          latitude: details.latitude,
          longitude: details.longitude,
          city: details.city ?? sub.split(',')[0]?.trim() ?? main,
          state: details.state,
          country: details.country ?? '',
          countryCode: details.countryCode,
          placeId: details.placeId ?? p.placeId,
        });
        setQuery('');
        setPredictions([]);
      } finally {
        setLoading(false);
        // Close Google's billing session; the next search starts a new one.
        sessionRef.current = newSessionToken();
      }
    },
    [onChange],
  );

  const clear = () => {
    onChange(null);
    setQuery('');
    setPredictions([]);
    sessionRef.current = newSessionToken();
  };

  if (value) {
    return (
      <div className={styles.selected}>
        <span className={styles.pin} aria-hidden="true">
          <Icon icon={LuMapPin} size={16} />
        </span>
        <div className={styles.selectedBody}>
          <p className={styles.selectedName}>{value.formattedAddress}</p>
          {(value.city || value.country) && (
            <p className={styles.selectedSub}>
              {[value.city, value.country].filter(Boolean).join(', ')}
            </p>
          )}
          {(value.latitude !== 0 || value.longitude !== 0) && (
            <p className={styles.selectedGps}>
              ✓ GPS captured: {value.latitude.toFixed(4)}°,{' '}
              {value.longitude.toFixed(4)}°
            </p>
          )}
        </div>
        <button type="button" className={styles.change} onClick={clear}>
          Change
        </button>
      </div>
    );
  }

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.inputWrap}>
        <span className={styles.icon} aria-hidden="true">
          <Icon icon={LuSearch} size={15} />
        </span>
        <input
          className={`${inputClassName} ${styles.input}`}
          placeholder={placeholder}
          value={query}
          onChange={(e) => search(e.target.value)}
          onFocus={() => predictions.length > 0 && setOpen(true)}
          aria-label={ariaLabel}
          aria-invalid={invalid}
          autoComplete="off"
        />
        {loading && <span className={styles.spinner} aria-hidden="true" />}
      </div>

      {open && (
        <div className={styles.popover}>
          {predictions.length === 0 ? (
            <p className={styles.empty}>No matching location</p>
          ) : (
            <ul className={styles.list}>
              {predictions.map((p) => {
                const { main, sub } = splitDescription(p.description);
                return (
                  <li key={p.placeId}>
                    <button
                      type="button"
                      className={styles.option}
                      // Keeps focus on the input so the blur doesn't close the
                      // popover before the click registers.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => select(p)}
                    >
                      <span className={styles.pin} aria-hidden="true">
                        <Icon icon={LuMapPin} size={14} />
                      </span>
                      <span className={styles.optionText}>
                        <span className={styles.optionMain}>{main}</span>
                        {sub && <span className={styles.optionSub}>{sub}</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <p className={styles.attribution}>Powered by Google</p>
        </div>
      )}
    </div>
  );
};
